import { Col, Row, Card, Spin } from "antd";
import React, { useState } from "react";
import { isCompanyUser, useLoginContext } from "@bindeleddet/core/contexts/login";
import { Link } from "react-router-dom";
import axios from "axios";
import { FavoriteJob, Job } from "@bindeleddet/core/types";
import dayjs from "dayjs";
import { StarOutlined, StarFilled, LoadingOutlined } from "@ant-design/icons";
import AlternativeLogo from "components/AlternativeLogo/AlternativeLogo";
import { CompanyLogo } from "@bindeleddet/core/components";
import { message } from "@bindeleddet/core/utils";
import { JOB_LIST_SCROLL_STORAGE_KEY } from "../helpers";
import { StyledText, StyledTitle } from "./style";
import RibbonStatusBadge from "./RibbonStatusBadge/RibbonStatusBadge";

interface Props {
  job: Job;
  favoriteJobs: FavoriteJob[];
  favoritesLoading: boolean;
  onFavoriteJobsChange: React.Dispatch<React.SetStateAction<FavoriteJob[]>>;
  // Only the main job postings list should save/restore scroll position - opt-in so the
  // other pages that render this card (favorites, a single company's job tab) don't write
  // a scroll position that's meaningless outside the main list. Defaults to false.
  enableScrollRestore?: boolean;
}

const SingleJobPostingBox: React.FC<Props> = ({
  job,
  favoriteJobs,
  favoritesLoading,
  onFavoriteJobsChange,
  enableScrollRestore = false,
}) => {
  const [mutatingFavorite, setMutatingFavorite] = useState(false);

  const { currentUser } = useLoginContext();
  const jobPath = `/jobs/${job.id}/`;

  const createFavJob = () => {
    if (!currentUser || isCompanyUser(currentUser)) return;
    setMutatingFavorite(true);
    axios
      .post<FavoriteJob>("/jobs/favorites/", {
        student: currentUser?.id,
        job: job.id,
      })
      .then(response => {
        message(
          "success",
          "Stillingsannonse lagt til i favoritter",
          'Du kan se en oversikt over dine favoritter under "Min profil"'
        );
        // Functional update: two favorites toggled in quick succession must not have the
        // second API response's local update clobber the first, since both start from
        // whatever favoriteJobs looked like at render time.
        onFavoriteJobsChange(previous => [...previous, { id: response.data.id, student: currentUser.id, job: job.id }]);
        setMutatingFavorite(false);
      })
      .catch(() => {
        message("error", "Kunne ikke legge til stillingsannonse i favoritter");
        setMutatingFavorite(false);
      });
  };

  const delFavJob = (jobId: number | undefined) => {
    setMutatingFavorite(true);
    const favId = favoriteJobs.find(fav => fav.job === jobId)?.id;
    axios
      .delete<FavoriteJob>(`/jobs/favorites/${favId}/`)
      .then(() => {
        message("success", "Stillingsannonse fjernet fra favoritter");
        onFavoriteJobsChange(previous => previous.filter(fav => fav.job !== jobId));
        setMutatingFavorite(false);
      })
      .catch(() => {
        message("error", "Kunne ikke fjerne stillingsannonse fra favoritter");
        setMutatingFavorite(false);
      });
  };

  const loading = favoritesLoading || mutatingFavorite;

  return (
    <>
      <Link
        to={jobPath}
        style={{ display: "block", color: "inherit", textDecoration: "none" }}
        onClick={() => {
          if (!enableScrollRestore) return;
          // Must happen here, before navigation starts - see the comment on the restore
          // effect in JobPostings.tsx for why an unmount-based save doesn't work.
          localStorage.setItem(JOB_LIST_SCROLL_STORAGE_KEY, String(window.scrollY));
        }}
      >
        <RibbonStatusBadge deadline={job.deadline} created_at={job.created_at}>
          <Card hoverable bodyStyle={{ padding: "0.5em", paddingLeft: "0.8em" }}>
            <Row>
              {/*
              A fixed pixel height (not "100%") is required here: CompanyLogo/AlternativeLogo
              size to 100% of this Col's height, but a percentage height only resolves against
              an ancestor with an explicitly defined height - without one it falls back to the
              logo's own intrinsic aspect ratio, so cards varied in height depending on each
              company's logo proportions. object-fit: contain (in both components) then scales
              any logo to fit this fixed box without distorting it.
              */}
              <Col span={4} style={{ height: "100px" }}>
                {job.alt_logo ? (
                  <AlternativeLogo size="100%" src={job.alt_logo} />
                ) : (
                  <CompanyLogo size="100%" companyId={job.company} src={job.company_logo ?? undefined} />
                )}
              </Col>
              <Col
                span={17}
                style={{ paddingLeft: "1em", lineHeight: "100%", paddingTop: "0.5em", paddingBottom: "0.5em" }}
              >
                <Row>
                  <StyledText>{job.company_name}</StyledText>
                </Row>
                <Row>
                  <StyledTitle level={4}>{job.title}</StyledTitle>
                </Row>
                <Row style={{ color: "#808080" }}>Søknadsfrist: {dayjs(job.deadline).format("DD.MM.YYYY")}</Row>
              </Col>
              <Col span={3} style={{ alignItems: "center", justifyContent: "center", display: "flex" }}>
                <div style={{ textAlign: "center", lineHeight: "100%" }}>
                  {loading ? (
                    <Spin indicator={<LoadingOutlined style={{ color: "#f3922d" }} spin />} />
                  ) : currentUser && !isCompanyUser(currentUser) ? (
                    favoriteJobs.some(fav => fav.job === job.id) ? (
                      <StarFilled
                        style={{ color: "#f3922d", fontSize: "1.7em", zIndex: 100 }}
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation(); // This prevents the card link. Only remove favorite.
                          delFavJob(job.id);
                        }}
                      />
                    ) : (
                      <StarOutlined
                        style={{ color: "#f3922d", fontSize: "1.7em" }}
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation(); // This prevents the card link. Only add favorite.
                          createFavJob();
                        }}
                      />
                    )
                  ) : null}
                </div>
              </Col>
            </Row>
          </Card>
        </RibbonStatusBadge>
      </Link>
    </>
  );
};

export default SingleJobPostingBox;
